import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildHttpParams } from '../../../core/utils/http-util';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../core/constants/endpoints';
import {
  IAutomation,
  IAutomationList,
  IAutomationListRaw,
  IAutomationRaw,
  IAutomationRunAccepted,
  IAutomationRunAcceptedRaw,
  ICreateAutomationRequest,
  IUpdateAutomationRequest,
  toAutomation,
  toAutomationList,
  toAutomationRunAccepted,
  toCreateAutomationRaw,
  toUpdateAutomationRaw
} from '../interfaces/automation.interface';

@Injectable({ providedIn: 'root' })
export class AutomationsApi {
  private readonly baseUrl = `${environment.ceppemAiApi}/${endpoints.ceppemAi.automations}`;

  constructor(private http: HttpClient) {}

  list(): Observable<IAutomationList> {
    return this.http
      .get<IAutomationListRaw>(this.baseUrl)
      .pipe(map(toAutomationList));
  }

  getById(automationId: string): Observable<IAutomation> {
    return this.http
      .get<IAutomationRaw>(`${this.baseUrl}/${encodeURIComponent(automationId)}`)
      .pipe(map(toAutomation));
  }

  create(request: ICreateAutomationRequest): Observable<IAutomation> {
    return this.http
      .post<IAutomationRaw>(this.baseUrl, toCreateAutomationRaw(request))
      .pipe(map(toAutomation));
  }

  update(
    automationId: string,
    request: IUpdateAutomationRequest
  ): Observable<IAutomation> {
    return this.http
      .patch<IAutomationRaw>(
        `${this.baseUrl}/${encodeURIComponent(automationId)}`,
        toUpdateAutomationRaw(request)
      )
      .pipe(map(toAutomation));
  }

  activate(automationId: string, version: number): Observable<IAutomation> {
    return this.http
      .post<IAutomationRaw>(
        `${this.baseUrl}/${encodeURIComponent(automationId)}/activate`,
        { version }
      )
      .pipe(map(toAutomation));
  }

  pause(automationId: string, version: number): Observable<IAutomation> {
    return this.http
      .post<IAutomationRaw>(
        `${this.baseUrl}/${encodeURIComponent(automationId)}/pause`,
        { version }
      )
      .pipe(map(toAutomation));
  }

  run(
    automationId: string,
    idempotencyKey: string
  ): Observable<IAutomationRunAccepted> {
    return this.http
      .post<IAutomationRunAcceptedRaw>(
        `${this.baseUrl}/${encodeURIComponent(automationId)}/run`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        { idempotency_key: idempotencyKey }
      )
      .pipe(map(toAutomationRunAccepted));
  }

  remove(automationId: string, version: number): Observable<void> {
    const params = buildHttpParams({ version });

    return this.http.delete<void>(
      `${this.baseUrl}/${encodeURIComponent(automationId)}`,
      { params }
    );
  }
}
